import { Link } from 'react-router-dom';
import MetaHead from '../../components/MetaHead';
import { imageUrl, srcSet } from '../../lib/images';

export default function WalkAndTalkPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      <MetaHead 
        title="Walk and talk - Din Privat Psykolog"
        description="Walk and talk terapi i den smukke natur omkring Gram Slotspark. Terapeutisk samtale i bevægelse ved autoriseret psykolog Marie Aagaard Larsen."
        ogTitle="Walk and talk - Din Privat Psykolog"
        ogDescription="Walk and talk terapi i den smukke natur omkring Gram Slotspark. Terapeutisk samtale i bevægelse ved autoriseret psykolog Marie Aagaard Larsen."
        ogImage="https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/41c11bff-6363-4fb2-936f-18581b6de4c8.jpg"
      />

      {/* Breadcrumb / Back Link */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-[#465A4F]">
        <Link to="/" className="hover:text-[#5f7365] underline font-medium">
          Forside
        </Link>
        <span>/</span>
        <span className="text-[#5f7365] font-semibold">Walk and talk</span>
      </div>

      {/* Header */}
      <div className="border-b border-[#E0D7C6] pb-8 space-y-4">
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#5f7365]">
          Walk and talk
        </h1>
        <p className="text-base sm:text-lg text-[#3B4D42] max-w-3xl leading-relaxed">
          Samtaleterapi i bevægelse i det fri, hvor naturens ro og den friske luft understøtter den terapeutiske proces.
        </p>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-6">
          <div className="space-y-4 text-sm sm:text-base text-[#5f7365] leading-relaxed">
            <h2 className="font-serif text-xl sm:text-2xl font-bold text-[#5f7365]">
              Hvorfor vælge samtale i bevægelse?
            </h2>
            <p>
              For nogle kan det at sidde over for en psykolog i et lukket lokale føles intenst eller overvældende. Ved <em>Walk and talk</em> går vi side om side i den smukke og rolige natur omkring Gram Slotspark.
            </p>
            <p>
              Når kroppen er i bevægelse, løsnes op for tanker og følelser. Det direkte øjenkontaktspres mindskes, og den friske luft giver et naturligt pusterum. Mange oplever, at samtalen glider mere frit og ubesværet under en gåtur.
            </p>

            <h3 className="font-serif text-lg font-bold text-[#5f7365] pt-2">
              Praktisk om Walk and talk
            </h3>
            <p>
              Vi mødes ved aftalt sted ved Gram Slotspark. Turen tilpasses altid dit tempo og vejrforholdene.
            </p>
            <ul className="space-y-2 list-disc pl-5 text-[#384A3F]">
              <li>Varighed: 45 minutter (eller efter aftale).</li>
              <li>Husk praktisk og varmt tøj samt behagelige gåsko.</li>
              <li>Sørg gerne for en regnjakke, hvis vejret er ustadigt – vi går i udgangspunktet uanset vejret, medmindre det stormer eller øser ned.</li>
            </ul>

            <div className="pt-2 space-y-2">
              <h4 className="font-serif text-base font-bold text-[#5f7365] flex items-center gap-2">
                <i className="fa-regular fa-tree text-[#5f7365]" aria-hidden="true" />
                Mødested
              </h4>
              <p className="text-xs sm:text-sm text-[#465A4F] leading-relaxed">
                Gram Slotspark, 6510 Gram. Nærmere mødested og parkeringsvejledning aftales ved booking.
              </p>
            </div>
          </div>

          <div className="pt-6 border-t border-[#EDE6D8] flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/kontakt"
              className="px-6 py-3.5 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <i className="fa-regular fa-calendar-check" aria-hidden="true" />
              <span>Aftal Walk and talk tid</span>
            </Link>
            <Link
              to="/priser"
              className="px-5 py-3.5 bg-white hover:bg-[#FAF8F3] text-[#5f7365] border border-[#D5CEBF] font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
            >
              <span>Se priser</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>

        {/* Right Info Box */}
        <div className="lg:col-span-4 bg-[#5f7365] text-white p-6 space-y-6">
          <div className="overflow-hidden border border-[#4e6053]">
            <img 
              src={imageUrl('41c11bff-6363-4fb2-936f-18581b6de4c8.jpg', 'width=800,quality=90,format=auto')}
              srcSet={srcSet('41c11bff-6363-4fb2-936f-18581b6de4c8.jpg', [400, 600, 800, 1000])}
              sizes="(min-width: 1024px) 380px, 100vw"
              alt="Skovbund og grøn natur under Walk and talk samtaleterapi"
              className="w-full h-64 sm:h-72 object-cover object-center"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="space-y-4">
            <h3 className="font-serif text-lg font-bold text-white">
              Ideelt for dig der ønsker:
            </h3>
            <ul className="space-y-2.5 text-xs sm:text-sm text-[#D5E3DB]">
              <li className="flex items-start gap-2">
                <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5" />
                <span>Samtale uden at skulle sidde stille over for hinanden.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5" />
                <span>Frisk luft og bevægelse kombineret med terapi.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5" />
                <span>En mere uformel og afslappet terapeutisk ramme.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
